import { type RefObject, useCallback, useLayoutEffect, useState } from 'react';

/**
 * Returns true when the element's content overflows its box
 * (`scrollHeight > offsetHeight` or `scrollWidth > offsetWidth`).
 * Re-checks on resize via ResizeObserver, and whenever `deps` change
 * (e.g. truncated text content).
 */
export const useIsOverflow = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  deps: unknown[] = []
): boolean => {
  const [isOverflow, setIsOverflow] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    setIsOverflow(
      element.scrollHeight > element.offsetHeight ||
        element.scrollWidth > element.offsetWidth
    );
  }, [ref]);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    checkOverflow();

    const observer = new ResizeObserver(() => {
      checkOverflow();
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref, checkOverflow, ...deps]);

  return isOverflow;
};
